import * as React from "react";
import { Icon } from "../actions/Icon";
import { Menu } from "../overlays/Menu";
import { MenuItem } from "../overlays/MenuItem";

/**
 * Tab strip. underline for page sections (Notes / Transcript / Recording), pill for side panels, solid for mobile.
 */
export interface TabsProps {
  items: { value: string; label: React.ReactNode; count?: React.ReactNode }[];
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  variant?: "underline" | "pill" | "solid";
  /** "menu": tabs that don't fit move into a "More" menu at the end of the strip. */
  overflow?: "menu";
  /** Rendered at the far right of the strip */
  trailing?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

const width = (el: Element | null | undefined) => (el ? el.getBoundingClientRect().width : 0);
const px = (v: string) => parseFloat(v) || 0;

/** How many leading tabs fit beside the More button, or all of them. `widths` are each tab's
 * width; `more` is the More button's width and `moreArrow` what its arrow adds to a label. */
export function fitTabs(
  avail: number,
  gap: number,
  widths: number[],
  activeIndex: number,
  more: number,
  moreArrow: number,
): number {
  const total = (n: number) => widths.slice(0, n).reduce((a, w) => a + w, 0) + gap * Math.max(0, n - 1);
  if (total(widths.length) <= avail) return widths.length;
  for (let k = widths.length - 1; k > 0; k--) {
    // A hidden active tab is shown on the More button, so it needs that label's width.
    const button = activeIndex >= k ? Math.max(more, widths[activeIndex] + moreArrow) : more;
    if (total(k) + gap + button <= avail) return k;
  }
  return 0;
}

export function Tabs({
  items = [],
  value,
  defaultValue,
  onChange,
  variant = "underline",
  overflow,
  trailing,
  className,
  style,
}: TabsProps): JSX.Element {
  const [inner, setInner] = React.useState<string | undefined>(
    defaultValue !== undefined ? defaultValue : items[0] && items[0].value,
  );
  const cur = value === undefined ? inner : value;
  const select = (v: string) => {
    if (value === undefined) setInner(v);
    onChange?.(v);
  };

  const stripRef = React.useRef<HTMLDivElement>(null);
  const probeRef = React.useRef<HTMLDivElement>(null);
  const trailingRef = React.useRef<HTMLSpanElement>(null);
  const moreRef = React.useRef<HTMLSpanElement>(null);
  const [shown, setShown] = React.useState(items.length);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const activeIndex = items.findIndex((it) => it.value === cur);
  // Callers usually pass a fresh items array each render; remeasure only when the tabs change.
  const itemsKey = items.map((it) => it.value).join("\u0000");
  const hasTrailing = Boolean(trailing);

  // Measure an invisible copy of every tab (and of More) and keep as many real tabs as fit.
  React.useLayoutEffect(() => {
    if (overflow !== "menu") return;
    const strip = stripRef.current;
    const probe = probeRef.current;
    if (!strip || !probe) return;
    const measure = () => {
      const cs = getComputedStyle(strip);
      const gap = px(cs.columnGap);
      const tabs = Array.from(probe.querySelectorAll("[data-probe-tab]"));
      const moreBtn = probe.querySelector("[data-probe-more]");
      const moreText = probe.querySelector("[data-probe-more-text]");
      const tail = trailingRef.current ? width(trailingRef.current) + gap : 0;
      const avail = strip.clientWidth - px(cs.paddingLeft) - px(cs.paddingRight) - tail;
      const more = width(moreBtn);
      setShown(fitTabs(avail, gap, tabs.map(width), activeIndex, more, more - width(moreText)));
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(strip);
    void document.fonts?.ready.then(measure);
    return () => ro.disconnect();
  }, [overflow, itemsKey, activeIndex, hasTrailing]);

  React.useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setMenuOpen(false);
        moreRef.current?.querySelector("button")?.focus();
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  const menu = overflow === "menu";
  const visible = menu ? items.slice(0, shown) : items;
  const hidden = menu ? items.slice(shown) : [];
  const activeHidden = hidden.some((it) => it.value === cur);
  const tabBody = (it: TabsProps["items"][number]) => (
    <>
      {it.label}
      {it.count !== undefined && <span className="gk-tab__count">{it.count}</span>}
    </>
  );

  return (
    <div
      ref={stripRef}
      role="tablist"
      className={["gk-tabs", "gk-tabs--" + variant, menu && "gk-tabs--menu", className].filter(Boolean).join(" ")}
      style={style}
    >
      {visible.map((it) => (
        <button
          key={it.value}
          type="button"
          role="tab"
          aria-selected={cur === it.value}
          className="gk-tab"
          onClick={() => select(it.value)}
        >
          {tabBody(it)}
        </button>
      ))}
      {hidden.length > 0 && (
        <span ref={moreRef} className="gk-tabs__more">
          <button
            type="button"
            role="tab"
            aria-selected={activeHidden}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            className="gk-tab"
            onClick={() => setMenuOpen((o) => !o)}
          >
            {activeHidden ? tabBody(items[activeIndex]) : "More"}
            <Icon name="caret-down" size={16} />
          </button>
          {menuOpen && (
            <Menu className="gk-tabs__menu">
              {hidden.map((it) => (
                <MenuItem
                  key={it.value}
                  role="menuitemradio"
                  aria-checked={cur === it.value}
                  onClick={() => {
                    setMenuOpen(false);
                    select(it.value);
                  }}
                >
                  {it.label}
                </MenuItem>
              ))}
            </Menu>
          )}
        </span>
      )}
      {trailing && (
        <span ref={trailingRef} style={{ marginLeft: "auto", display: "flex", alignItems: "center" }}>
          {trailing}
        </span>
      )}
      {menu && (
        <div ref={probeRef} className="gk-tabs__probe" aria-hidden="true">
          {items.map((it) => (
            <span key={it.value} data-probe-tab className="gk-tab" aria-selected={cur === it.value}>
              {tabBody(it)}
            </span>
          ))}
          <span data-probe-more className="gk-tab">
            <span data-probe-more-text>More</span>
            <Icon name="caret-down" size={16} />
          </span>
        </div>
      )}
    </div>
  );
}
